// Orbital Button. Five variants × three sizes. Polymorphic via `as`.

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
type Size = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
}

const variantStyles: Record<Variant, string> = {
  primary:
    'bg-accent text-accent-fg hover:bg-accent/90 active:bg-accent/80 shadow-glow-accent disabled:bg-accent/50 disabled:shadow-none',
  secondary:
    'bg-bg-elevated text-fg border border-border hover:bg-bg-hover hover:border-border-strong disabled:opacity-50',
  ghost:
    'bg-transparent text-fg hover:bg-bg-hover disabled:opacity-50',
  danger:
    'bg-danger text-white hover:bg-danger/90 disabled:opacity-50',
  subtle:
    'bg-bg-inset text-fg border border-border/60 hover:bg-bg-hover hover:border-border disabled:opacity-50',
};

const sizeStyles: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { className, variant = 'secondary', size = 'md', fullWidth, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-md transition-colors duration-150',
        'disabled:cursor-not-allowed',
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    />
  );
});
